import { useState, useEffect } from 'react';
import {
  Building2, Loader2, AlertCircle, CheckCircle, Save,
  Image, Video, Users, Award, FileCheck,
  Shield, Edit3, X, Plus, Trash2
} from 'lucide-react';
import { Input, Button, MessagePlugin } from 'tdesign-react';
import ProductImageUploader from '../../components/ProductImageUploader';
import ProductRichTextEditor from '../../components/ProductRichTextEditor';
import { enterpriseInfoApi } from '../../api';
import { tokenStorage } from '../../api';
import type { EnterpriseInfoVO, RichTextItem } from '@/types/eCard';
import { ImagePreviewProvider, PreviewableImage } from './components/ImagePreview';

const emptyRichItem = (): RichTextItem => ({
  title: '',
  image: '',
  description: '',
});

const defaultInfo: EnterpriseInfoVO = {
  enterpriseName: '',
  logo: '',
  introduction: '',
  carouselImages: [],
  promoVideo: '',
  officialAccount: '',
  honors: [],
  qualifications: [],
  scaleStrength: [],
  endorsements: [],
};

type ListType = 'honors' | 'qualifications' | 'scaleStrength' | 'endorsements';

const listConfig: { key: ListType; label: string; icon: any; color: string; bgColor: string }[] = [
  { key: 'honors', label: '企业荣誉', icon: Award, color: 'text-purple-600', bgColor: 'bg-purple-50 border-purple-200' },
  { key: 'qualifications', label: '经营资质', icon: FileCheck, color: 'text-blue-600', bgColor: 'bg-blue-50 border-blue-200' },
  { key: 'scaleStrength', label: '规模实力', icon: Building2, color: 'text-green-600', bgColor: 'bg-green-50 border-green-200' },
  { key: 'endorsements', label: '合作背书', icon: Shield, color: 'text-orange-600', bgColor: 'bg-orange-50 border-orange-200' },
];

function SectionCard({
  title,
  icon: Icon,
  children,
  action
}: {
  title: React.ReactNode;
  icon: any;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <Icon className="w-5 h-5 text-[#1c5bff]" />
          {title}
        </h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function DisplayRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm text-gray-800 font-medium text-right max-w-[60%] truncate">{value || '-'}</span>
    </div>
  );
}

function RichItemEdit({
  item,
  onChange,
  onRemove,
  colorClass
}: {
  item: RichTextItem;
  onChange: (field: keyof RichTextItem, val: any) => void;
  onRemove: () => void;
  colorClass: string;
}) {
  return (
    <div className={`flex gap-3 items-start p-3 rounded-lg border ${colorClass} transition-all`}>
      <div className="flex-1">
        <ProductImageUploader
          value={item.image}
          onChange={(val) => onChange('image', val)}
          simple
        />
      </div>
      <div className="flex-1 space-y-2">
        <Input
          value={item.title}
          onChange={(val) => onChange('title', val)}
          placeholder="标题"
        />
        <ProductRichTextEditor
          value={item.description}
          onChange={(val) => onChange('description', val)}
          showHeight={false}
        />
      </div>
      <button
        onClick={onRemove}
        className="p-1.5 text-gray-400 hover:text-white hover:bg-red-500 rounded-lg mt-2 transition-colors"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

function RichItemDisplay({ item }: { item: RichTextItem }) {
  return (
    <div className="flex gap-3 items-start p-3 rounded-lg border border-gray-100 bg-gray-50/50">
      <div className="w-[120px] h-[120px]">
        {item.image ? (
          <PreviewableImage src={item.image} alt={item.title} className="w-full h-full object-cover rounded-lg" />
        ) : (
          <div className="w-full h-full rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
            暂无图片
          </div>
        )}
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-800">{item.title || '未设置标题'}</div>
        <div
          className="text-sm text-gray-500 line-clamp-3"
          dangerouslySetInnerHTML={{ __html: item.description || '<span class="text-gray-400">暂无描述</span>' }}
        />
      </div>
    </div>
  );
}

export default function HomepageConfiguration() {
  const [info, setInfo] = useState<EnterpriseInfoVO>(defaultInfo);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await enterpriseInfoApi.getInfo();
      if (res.code === 200 && res.data) {
        const data = res.data;
        // 兼容 carouselImages 可能是字符串的情况
        const rawCarousel = data.carouselImages;
        const carouselArr: string[] = Array.isArray(rawCarousel)
          ? rawCarousel
          : (typeof rawCarousel === 'string' ? (rawCarousel as string).split(',').filter(Boolean) : []);
        setInfo({
          ...defaultInfo,
          ...data,
          honors: data.honors || [],
          qualifications: data.qualifications || [],
          scaleStrength: data.scaleStrength || [],
          endorsements: data.endorsements || [],
          carouselImages: carouselArr,
        });
        setHasData(true);
        setIsEditing(false);
      } else {
        setHasData(false);
        setIsEditing(true);
      }
    } catch {
      setHasData(false);
      setIsEditing(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const nameStr = typeof info.enterpriseName === 'string' ? info.enterpriseName.trim() : '';
    const logoStr = typeof info.logo === 'string' ? info.logo.trim() : '';
    const introStr = typeof info.introduction === 'string' ? info.introduction.trim() : '';

    if (!nameStr) {
      setError('请输入企业名称');
      return;
    }
    if (!logoStr) {
      setError('请上传企业Logo');
      return;
    }
    if (!introStr) {
      setError('请输入企业简介');
      return;
    }
    const rawCarousel = info.carouselImages;
    const carouselArr: string[] = Array.isArray(rawCarousel)
      ? rawCarousel
      : (typeof rawCarousel === 'string' ? (rawCarousel as string).split(',').filter(Boolean) : []);
    if (carouselArr.length === 0) {
      setError('请上传轮播图');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      // 过滤掉空值字段，避免将空数据传给后端
      const cleanRichItems = (items: RichTextItem[]) =>
        items
          .map((item) => ({
            ...item,
            title: item.title?.trim() || '',
            image: item.image?.trim() || '',
            description: item.description?.trim() || '',
          }))
          .filter((item) => item.title || item.image || item.description);

      const submitData: EnterpriseInfoVO = {
        ...info,
        enterpriseName: info.enterpriseName?.trim?.() || '',
        logo: info.logo?.trim?.() || '',
        introduction: info.introduction?.trim?.() || '',
        promoVideo: typeof info.promoVideo === 'string' ? info.promoVideo.trim() : '',
        officialAccount: info.officialAccount?.trim?.() || '',
        carouselImages: carouselArr.filter((img) => img && img.trim()),
        honors: cleanRichItems(info.honors || []),
        qualifications: cleanRichItems(info.qualifications || []),
        scaleStrength: cleanRichItems(info.scaleStrength || []),
        endorsements: cleanRichItems(info.endorsements || []),
      };
      const res = await enterpriseInfoApi.updateInfo(submitData);
      if (res.code === 200 && res.data) {
        setInfo(res.data);
        setHasData(true);
        setIsEditing(false);
        setMessage('保存成功');
        setTimeout(() => setMessage(''), 3000);
        MessagePlugin.success('保存成功');
      } else {
        setError(res.message || '保存失败');
      }
    } catch (err: any) {
      setError(err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasData) {
      setIsEditing(false);
    } else {
      loadData();
    }
  };

  const updateField = (field: keyof EnterpriseInfoVO, value: any) => {
    setInfo((prev) => ({ ...prev, [field]: value }));
  };

  const addListItem = (type: ListType) => {
    setInfo((prev) => ({
      ...prev,
      [type]: [...prev[type], emptyRichItem()],
    }));
  };

  const removeListItem = (type: ListType, index: number) => {
    setInfo((prev) => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index),
    }));
  };

  const updateListItem = (type: ListType, index: number, field: keyof RichTextItem, value: any) => {
    setInfo((prev) => ({
      ...prev,
      [type]: prev[type].map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#1c5bff] animate-spin" />
      </div>
    );
  }

  const showEditActions = isEditing;
  const showReadOnlyActions = !isEditing && hasData;

  return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#1c5bff]" />
          首页配置
          </h1>
          {showReadOnlyActions && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] transition-colors flex items-center gap-2 text-sm"
            >
              <Edit3 className="w-4 h-4" />
              编辑
            </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}
      {message && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center gap-2 text-green-600 text-sm">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          {message}
        </div>
      )}

      <div className="space-y-4">
        {/* 企业基本信息 */}
        <SectionCard title="企业基本信息" icon={Building2}>
          {!isEditing ? (
            <div className="divide-y divide-gray-50">
              <DisplayRow label="企业名称" value={info.enterpriseName} />
              <DisplayRow label="企业Logo" value={
                info.logo ? (
                  <PreviewableImage src={info.logo} alt="logo" className="h-12 rounded" />
                ) : '-'
              } />
              <div className="py-2">
                <span className="text-sm text-gray-500">企业简介</span>
                <div
                  className="mt-1 text-sm text-gray-700 line-clamp-4"
                  dangerouslySetInnerHTML={{ __html: info.introduction || '<span class="text-gray-400">暂无简介</span>' }}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">企业名称 <span className="text-red-500">*</span></label>
                <Input
                  value={info.enterpriseName}
                  onChange={(val) => updateField('enterpriseName', val)}
                  placeholder="请输入企业名称"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">企业Logo <span className="text-red-500">*</span></label>
                <ProductImageUploader
                  value={info.logo}
                  onChange={(val) => updateField('logo', val)}
                  simple
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">企业简介 <span className="text-red-500">*</span></label>
                <ProductRichTextEditor
                  value={info.introduction}
                  onChange={(val) => updateField('introduction', val)}
                />
              </div>
            </div>
          )}
        </SectionCard>

        {/* 轮播图 */}
        <SectionCard title={<span>轮播图 <span className="text-red-500">*</span></span>} icon={Image}>
          {!isEditing ? (
            <div>
              {info.carouselImages.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {info.carouselImages.map((img, idx) => (
                    <PreviewableImage
                      key={idx}
                      src={img}
                      images={info.carouselImages}
                      index={idx}
                      className="w-full h-32 rounded-lg object-cover"
                    />
                  ))}
                </div>
              ) : (
                <span className="text-sm text-gray-400">暂无轮播图</span>
              )}
            </div>
          ) : (
            <ProductImageUploader
              value={info.carouselImages}
              onChange={(val) => updateField('carouselImages', val)}
            />
          )}
        </SectionCard>

        {/* 宣传视频 */}
        <SectionCard title="宣传视频" icon={Video}>
          {!isEditing ? (
            <div>
              {info.promoVideo ? (
                <video
                  src={info.promoVideo}
                  controls
                  className="w-full max-w-md rounded-lg border border-gray-200"
                />
              ) : (
                <span className="text-sm text-gray-400">暂未上传</span>
              )}
            </div>
          ) : (
            <ProductImageUploader
              value={info.promoVideo}
              onChange={(val) => updateField('promoVideo', val)}
              simple
              uploadType={2}
            />
          )}
        </SectionCard>

        {/* 公众号配置 */}
        <SectionCard title="公众号配置" icon={Users}>
          {!isEditing ? (
            <div className="divide-y divide-gray-50">
              <DisplayRow label="公众号地址" value={info.officialAccount} />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">公众号地址</label>
              <Input
                value={info.officialAccount}
                onChange={(val) => updateField('officialAccount', val)}
                placeholder="请输入公众号地址"
              />
            </div>
          )}
        </SectionCard>

        {/* 荣誉/资质/实力/背书 列表 */}
        {listConfig.map(({ key, label, icon: Icon, color, bgColor }) => (
          <SectionCard
            key={key}
            title={label}
            icon={Icon}
            action={
              isEditing ? (
                <Button
                  size="small"
                  theme="primary"
                  variant="outline"
                  icon={<Plus className="w-3 h-3" />}
                  onClick={() => addListItem(key)}
                >
                  添加
                </Button>
              ) : null
            }
          >
            {!isEditing ? (
              <div className="space-y-3">
                {info[key].length > 0 ? (
                  info[key].map((item, idx) => (
                    <RichItemDisplay key={idx} item={item} />
                  ))
                ) : (
                  <span className="text-sm text-gray-400">暂无数据</span>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {info[key].length > 0 ? (
                  info[key].map((item, idx) => (
                    <RichItemEdit
                      key={idx}
                      item={item}
                      onChange={(field, val) => updateListItem(key, idx, field, val)}
                      onRemove={() => removeListItem(key, idx)}
                      colorClass={bgColor}
                    />
                  ))
                ) : (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-400">暂无{label}，点击右上角【添加】按钮新增</p>
                  </div>
                )}
              </div>
            )}
          </SectionCard>
        ))}

        {/* 底部操作区 */}
        {showEditActions && (
          <div className="sticky bottom-0 bg-white border-t border-gray-100 rounded-xl shadow-sm p-4 flex items-center justify-end gap-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              取消
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              保存
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
